import { Scenario, RepaymentType, StrategyType, CalculationMode } from '../domain/types.js';
import { LumpEntry, readLumps, setLumps } from './lump-list.js';
import { readRecalculations, setRecalculations } from './recalculation-list.js';
import { formatMoneyInput, parseMoney, rubToCents, monthsBetween, getMonthDate, clamp } from './form-values.js';
import { formatPercentInput, parsePercent } from './input-format.js';

export { formatMoneyInput, parseMoney, rubToCents, monthsBetween, getMonthDate, clamp };

export type FormState = {
  scenarioName: string;
  principal: number;
  termYears: number;
  rateApr: number;
  startDate: string;
  repaymentType: RepaymentType;
  strategy: StrategyType;
  calculationMode?: CalculationMode;
  bankDisbursementDate?: string;
  bankPaymentDay?: number;
  bankRecalculations?: string[]; // YYYY-MM of the first payment with a recalculated amount
  discountEnabled: boolean;
  discountStart: string;
  discountEnd: string;
  discountRate: number;
  overpaymentEnabled: boolean;
  overpaymentType: 'fixed' | 'adaptive';
  recurringAmount: number;
  recurringStart: string;
  recurringEnd?: string;
  adaptiveTargetAmount: number;
  adaptiveMinAmount: number;
  adaptiveStart: string;
  adaptiveEnd?: string;
  lumpEnabled: boolean;
  /** One-off overpayments (form rows); rows with a zero amount are kept here but not built. */
  lumps: LumpEntry[];
  refinanceEnabled: boolean;
  refinanceMonth: string;
  refinanceRate: number;
  refinanceTerm: number;
  refinanceRepayment: RepaymentType;
};

export function populateFormFromScenario(form: HTMLFormElement, scenario: Scenario) {
  // Basic Fields
  setVal(form, 'scenarioName', scenario.name);
  setMoney(form, 'principal', scenario.principal / 100);
  setVal(form, 'termYears', Math.round(scenario.termMonths / 12));
  setPercent(form, 'rateApr', scenario.rateApr);
  setVal(form, 'startDate', scenario.startDate);
  setRadio(form, 'repaymentType', scenario.repaymentType);
  setRadio(form, 'strategy', scenario.overpaymentStrategy);
  setRadio(form, 'calculationMode', scenario.calculationMode ?? 'monthly');
  setVal(form, 'bankDisbursementDate', scenario.bankCalendar?.disbursementDate ?? '');
  setVal(form, 'bankPaymentDay', scenario.bankCalendar?.paymentDay ?? 8);
  setRecalculations(form, (scenario.bankCalendar?.recalculationMonths ?? [])
    .map(month => getMonthDate(scenario.startDate, month)));

  populateDiscount(form, scenario);
  populateOverpayments(form, scenario);
  populateLump(form, scenario);
  populateRefinance(form, scenario);
}

function populateDiscount(form: HTMLFormElement, scenario: Scenario) {
  const discount = scenario.ratePeriods.length > 0 ? scenario.ratePeriods[0] : null;
  setCheck(form, 'discountEnabled', Boolean(discount));
  if (discount) {
    setVal(form, 'discountStart', getMonthDate(scenario.startDate, discount.startMonth));
    setVal(form, 'discountEnd', getMonthDate(scenario.startDate, discount.endMonth));
    setPercent(form, 'discountRate', discount.rateApr);
  }
}

function populateOverpayments(form: HTMLFormElement, scenario: Scenario) {
  const hasAdaptive = scenario.overpayments.some(o => o.adaptive);
  const hasFixed = scenario.overpayments.some(o => !o.adaptive && o.recurring);

  setCheck(form, 'overpaymentEnabled', hasAdaptive || hasFixed);
  if (hasAdaptive) {
    setRadio(form, 'overpaymentType', 'adaptive');
    const op = scenario.overpayments.find(o => o.adaptive)!;
    setVal(form, 'adaptiveStart', getMonthDate(scenario.startDate, op.month));
    setMoney(form, 'adaptiveTargetAmount', (op.adaptive?.targetAmount || 0) / 100);
    setMoney(form, 'adaptiveMinAmount', (op.adaptive?.minAmount || 0) / 100);
    if (op.adaptive?.endMonth) {
      setVal(form, 'adaptiveEnd', getMonthDate(scenario.startDate, op.adaptive.endMonth));
    }
  } else if (hasFixed) {
    setRadio(form, 'overpaymentType', 'fixed');
    const op = scenario.overpayments.find(o => o.recurring)!;
    setVal(form, 'recurringStart', getMonthDate(scenario.startDate, op.month));
    setMoney(form, 'recurringAmount', op.amount / 100);
    if (op.recurring?.endMonth) {
      setVal(form, 'recurringEnd', getMonthDate(scenario.startDate, op.recurring.endMonth));
    }
  }
}

function populateLump(form: HTMLFormElement, scenario: Scenario) {
  const lumps = scenario.overpayments
    .filter(o => !o.recurring && !o.adaptive)
    .sort((a, b) => a.month - b.month)
    .map(o => ({ month: getMonthDate(scenario.startDate, o.month), amount: o.amount / 100 }));
  // Rows first, so the toggle's change handler sees a non-empty list and adds no default row.
  setLumps(form, lumps);
  setCheck(form, 'lumpEnabled', lumps.length > 0);
}

function populateRefinance(form: HTMLFormElement, scenario: Scenario) {
  const refi = scenario.refinances.length > 0 ? scenario.refinances[0] : null;
  setCheck(form, 'refinanceEnabled', Boolean(refi));
  if (refi) {
    setVal(form, 'refinanceMonth', getMonthDate(scenario.startDate, refi.month));
    setPercent(form, 'refinanceRate', refi.newRateApr);
    setVal(form, 'refinanceTerm', Math.round(refi.newTermMonths / 12));
    setRadio(form, 'refinanceRepayment', refi.repaymentType || 'fixed');
  }
}

function setMoney(form: HTMLFormElement, name: string, rubles: number) {
  setVal(form, name, formatMoneyInput(rubles));
}

function setPercent(form: HTMLFormElement, name: string, rate: number) {
  setVal(form, name, formatPercentInput(rate));
}

/** Percent field as a number in 0–50; `fallback` when empty or not a number. */
function readPercent(form: HTMLFormElement, name: string, fallback: number): number {
  const parsed = parsePercent(getInputValue(form, name, ''));
  return clamp(Number.isNaN(parsed) ? fallback : parsed, 0, 50);
}

function setVal(form: HTMLFormElement, name: string, val: string | number) {
  const input = form.elements.namedItem(name) as HTMLInputElement | null;
  if (input) {
    input.value = String(val);
    input.dispatchEvent(new Event('input', { bubbles: true })); // Notify custom pickers if any
  }
}

function setCheck(form: HTMLFormElement, name: string, val: boolean) {
  const input = form.elements.namedItem(name) as HTMLInputElement | null;
  if (input) {
    input.checked = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function setRadio(form: HTMLFormElement, name: string, val: string) {
  const radios = form.querySelectorAll(`input[name="${name}"]`);
  radios.forEach((r) => {
    const el = r as HTMLInputElement;
    el.checked = (el.value === val);
    if (el.checked) el.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

export function readFormState(form: HTMLFormElement, fallbackState: FormState | null): FormState {
  const rawPrincipal = getInputValue(form, 'principal', '0');
  const validPrincipal = parseMoney(rawPrincipal) || 0;

  return {
    scenarioName: getInputValue(form, 'scenarioName', 'Сценарий'),
    principal: validPrincipal,
    termYears: clamp(parseFloat(getInputValue(form, 'termYears', '20')), 1, 40),
    rateApr: readPercent(form, 'rateApr', 10),
    startDate: getInputValue(form, 'startDate', fallbackState?.startDate ?? '2024-01'),
    repaymentType: (getCheckedValue(form, 'repaymentType') as RepaymentType) ?? 'fixed',
    strategy: (getCheckedValue(form, 'strategy') as StrategyType) ?? 'reduce_term',
    calculationMode: (getCheckedValue(form, 'calculationMode') || 'bank_calendar') as CalculationMode,
    bankDisbursementDate: getInputValue(form, 'bankDisbursementDate', ''),
    bankPaymentDay: Number(getInputValue(form, 'bankPaymentDay', '8')),
    bankRecalculations: readRecalculations(form),
    discountEnabled: getCheckbox(form, 'discountEnabled'),
    discountStart: getInputValue(form, 'discountStart', fallbackState?.discountStart ?? '2024-01'),
    discountEnd: getInputValue(form, 'discountEnd', fallbackState?.discountEnd ?? '2025-01'),
    discountRate: readPercent(form, 'discountRate', 6.5),
    overpaymentEnabled: getCheckbox(form, 'overpaymentEnabled'),
    overpaymentType: (getCheckedValue(form, 'overpaymentType') as 'fixed' | 'adaptive') ?? 'fixed',
    recurringAmount: parseMoney(getInputValue(form, 'recurringAmount', '0')),
    recurringStart: getInputValue(form, 'recurringStart', fallbackState?.recurringStart ?? '2025-01'),
    recurringEnd: getInputValue(form, 'recurringEnd', fallbackState?.recurringEnd ?? ''),
    adaptiveTargetAmount: parseMoney(getInputValue(form, 'adaptiveTargetAmount', '0')),
    adaptiveMinAmount: parseMoney(getInputValue(form, 'adaptiveMinAmount', '0')),
    adaptiveStart: getInputValue(form, 'adaptiveStart', fallbackState?.adaptiveStart ?? '2025-01'),
    adaptiveEnd: getInputValue(form, 'adaptiveEnd', fallbackState?.adaptiveEnd ?? ''),
    lumpEnabled: getCheckbox(form, 'lumpEnabled'),
    lumps: readLumps(form),
    refinanceEnabled: getCheckbox(form, 'refinanceEnabled'),
    refinanceMonth: getInputValue(form, 'refinanceMonth', fallbackState?.refinanceMonth ?? '2030-01'),
    refinanceRate: readPercent(form, 'refinanceRate', 8.5),
    refinanceTerm: clamp(parseFloat(getInputValue(form, 'refinanceTerm', '15')), 1, 40),
    refinanceRepayment: (getCheckedValue(form, 'refinanceRepayment') as RepaymentType) ?? 'fixed',
  };
}

export function buildScenario(state: FormState): Scenario {
  const scenario: Scenario = {
    id: 'interactive',
    name: state.scenarioName || 'Мой сценарий',
    principal: rubToCents(state.principal),
    termMonths: Math.round(state.termYears * 12),
    rateApr: state.rateApr,
    repaymentType: state.repaymentType,
    startDate: state.startDate,
    ratePeriods: [],
    overpayments: [],
    refinances: [],
    overpaymentStrategy: state.strategy,
  };

  if (state.calculationMode === 'bank_calendar') {
    scenario.calculationMode = 'bank_calendar';
    // Sorted and de-duplicated: the order of the rows does not matter to the schedule.
    const recalculationMonths = [...new Set((state.bankRecalculations ?? [])
      .map(month => monthsBetween(state.startDate, month)))].sort((a, b) => a - b);
    scenario.bankCalendar = {
      disbursementDate: state.bankDisbursementDate ?? '',
      paymentDay: state.bankPaymentDay ?? 8,
      ...(recalculationMonths.length ? { recalculationMonths } : {}),
    };
  }

  if (state.discountEnabled) {
    const start = Math.max(0, monthsBetween(state.startDate, state.discountStart));
    const end = Math.max(start, monthsBetween(state.startDate, state.discountEnd));
    scenario.ratePeriods.push({ startMonth: start, endMonth: end, rateApr: state.discountRate });
  }

  if (state.overpaymentEnabled) {
    if (state.overpaymentType === 'fixed' && state.recurringAmount > 0) {
      const month = Math.max(0, monthsBetween(state.startDate, state.recurringStart));
      const endMonth = state.recurringEnd ? Math.max(month, monthsBetween(state.startDate, state.recurringEnd)) : undefined;
      scenario.overpayments.push({
        month,
        amount: rubToCents(state.recurringAmount),
        recurring: { endMonth, step: 1 },
      });
    } else if (state.overpaymentType === 'adaptive' && state.adaptiveTargetAmount > 0) {
      const month = Math.max(0, monthsBetween(state.startDate, state.adaptiveStart));
      const endMonth = state.adaptiveEnd ? Math.max(month, monthsBetween(state.startDate, state.adaptiveEnd)) : undefined;
      scenario.overpayments.push({
        month,
        amount: 0, // Will be calculated dynamically
        adaptive: {
          targetAmount: rubToCents(state.adaptiveTargetAmount),
          minAmount: rubToCents(state.adaptiveMinAmount),
          endMonth,
          step: 1,
        },
      });
    }
  }

  if (state.lumpEnabled) {
    state.lumps
      .filter(lump => lump.amount > 0 && lump.month)
      .forEach(lump => {
        scenario.overpayments.push({
          month: Math.max(0, monthsBetween(state.startDate, lump.month)),
          amount: rubToCents(lump.amount),
        });
      });
  }

  if (state.refinanceEnabled) {
    scenario.refinances.push({
      month: Math.max(1, monthsBetween(state.startDate, state.refinanceMonth)),
      newRateApr: state.refinanceRate,
      newTermMonths: Math.round(state.refinanceTerm * 12),
      repaymentType: state.refinanceRepayment,
    });
  }

  return scenario;
}

export function getInputValue(form: HTMLFormElement, name: string, fallback: string): string {
  const field = form.elements.namedItem(name) as HTMLInputElement | null;
  return field?.value?.trim() || fallback;
}

export function getCheckedValue(form: HTMLFormElement, name: string): string {
  const group = form.querySelectorAll(`input[name="${name}"]`);
  const checked = Array.from(group).find(el => (el as HTMLInputElement).checked) as HTMLInputElement | undefined;
  return checked?.value || '';
}

export function getCheckbox(form: HTMLFormElement, name: string): boolean {
  const element = form.elements.namedItem(name) as HTMLInputElement | null;
  return Boolean(element?.checked);
}

